import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faStore, faVideo } from '@fortawesome/free-solid-svg-icons';
import { SEOHelmet } from 'components/SEO';
import { SegmentedToggle } from 'components/SegmentedToggle/SegmentedToggle';
import { useCreateTournamentMutation } from 'store/services/play/playApi';
import type { CreateTournamentBody, VenueType } from 'types/interfaces/play';
import {
  OTHER_FORMAT_LABEL,
  PLAY_FORMAT_OPTIONS,
  ROUND_DEFAULT_MINUTES,
  ROUND_MAX_MINUTES,
  ROUND_MIN_MINUTES,
  ROUND_MINUTES_STEP,
  STRUCTURE_OPTIONS,
  SWISS_ROUNDS_MAX,
  SWISS_ROUNDS_MIN,
  TournamentStructureValue,
} from 'constants/play';
import { recommendedSwissRounds } from 'utils/play/recommendedRounds';
import { playErrorMessage } from 'utils/play/playErrors';
import { playPath } from 'utils/play/inviteUrl';
import { PlayLayout } from '../../layout/PlayLayout';
import { CountStepper } from '../../components/CountStepper';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import { PlayToggle } from '../../components/PlayToggle';
import { formatClock } from '../../playFormat';
import './HostCreate.css';

const pad = (n: number) => String(n).padStart(2, '0');

/** Default start: the next full hour, in the TO's local zone, as date and time input values. */
const defaultStart = (): { date: string; time: string } => {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};

/** Local date + time inputs to ISO UTC (the Date constructor reads them in the browser's zone). */
export const toStartsAtIso = (date: string, time: string): string | null => {
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * Decision 7: tournament creation, fields in the spec order. Swiss rounds
 * default to 3 (recommendedSwissRounds(0), ruling 5); the lobby offers the
 * player-count recommendation once people have joined. Payload carries
 * startsAt as ISO UTC plus the TO's IANA timezone and the format LABEL.
 */
const HostCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [create, { isLoading }] = useCreateTournamentMutation();
  const [error, setError] = useState<string | null>(null);

  const initial = useMemo(defaultStart, []);
  const [name, setName] = useState('');
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [venueType, setVenueType] = useState<VenueType>('STORE');
  const [venueText, setVenueText] = useState('');
  const [format, setFormat] = useState<string>('Modern');
  const [customFormatName, setCustomFormatName] = useState('');
  const [structure, setStructure] = useState<TournamentStructureValue>('SWISS');
  const [topCutSize, setTopCutSize] = useState<4 | 8>(8);
  const [swissRounds, setSwissRounds] = useState(recommendedSwissRounds(0));
  const [roundMinutes, setRoundMinutes] = useState(ROUND_DEFAULT_MINUTES);
  const [decklistRequired, setDecklistRequired] = useState(true);
  const [lateRegistration, setLateRegistration] = useState(true);
  const [publicPage, setPublicPage] = useState(true);

  const isOther = format === OTHER_FORMAT_LABEL;
  const usesSwiss = structure === 'SWISS' || structure === 'SWISS_TOP_CUT';
  const startsAt = toStartsAtIso(date, time);
  const canSubmit = name.trim().length > 0 && !!startsAt && venueText.trim().length > 0 && (!isOther || customFormatName.trim().length > 0) && !isLoading;

  const submit = async () => {
    if (!canSubmit || !startsAt) return;
    setError(null);
    const body: CreateTournamentBody = {
      name: name.trim(),
      startsAt,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      venueType,
      venueText: venueText.trim(),
      format,
      customFormatName: isOther ? customFormatName.trim() : undefined,
      structure,
      topCutSize: structure === 'SWISS_TOP_CUT' ? topCutSize : null,
      swissRounds,
      roundMinutes,
      decklistRequired,
      lateRegistration,
      publicPage,
    };
    try {
      const created = await create(body).unwrap();
      navigate(playPath(created.tournament.inviteCode), { replace: true });
    } catch (err) {
      setError(playErrorMessage(err, "Couldn't create the tournament. Please try again."));
    }
  };

  return (
    <PlayLayout title="New tournament" backTo="/play" backIcon="close">
      <SEOHelmet title="Host a tournament - TradeMagic Play" description="Create a Magic tournament and get a shareable invite link." noindex />
      <form
        className="play-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="play-field">
          <label className="play-field__label" htmlFor="play-t-name">
            Name
          </label>
          <input id="play-t-name" className="play-native-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Thursday Night Modern" maxLength={80} />
        </div>

        <div className="play-field">
          <span className="play-field__label">Starts</span>
          <div className="play-form__row">
            <input className="play-native-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Start date" />
            <input className="play-native-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Start time" step={300} />
          </div>
        </div>

        <div className="play-field">
          <span className="play-field__label">Location</span>
          <SegmentedToggle<VenueType>
            ariaLabel="Location type"
            className="segmented-toggle--panel play-segmented"
            value={venueType}
            onChange={setVenueType}
            options={[
              { value: 'STORE', label: 'In person', icon: <FontAwesomeIcon icon={faStore} /> },
              { value: 'ONLINE', label: 'Online', icon: <FontAwesomeIcon icon={faVideo} /> },
            ]}
          />
          <div className={venueType === 'STORE' ? 'play-input-wrap' : undefined} style={{ marginTop: 8 }}>
            {venueType === 'STORE' && <FontAwesomeIcon icon={faLocationDot} />}
            <input
              className="play-native-input"
              value={venueText}
              onChange={(e) => setVenueText(e.target.value)}
              placeholder={venueType === 'STORE' ? 'Store name or address' : 'Discord server or SpellTable link'}
              aria-label={venueType === 'STORE' ? 'Store name or address' : 'Discord server or SpellTable link'}
              maxLength={200}
            />
          </div>
        </div>

        <div className="play-field">
          <label className="play-field__label" htmlFor="play-t-format">
            Format
          </label>
          <select id="play-t-format" className="play-native-select" value={format} onChange={(e) => setFormat(e.target.value)}>
            {PLAY_FORMAT_OPTIONS.map((f) => (
              <option key={f.value} value={f.label}>
                {f.label}
              </option>
            ))}
            <option value={OTHER_FORMAT_LABEL}>{OTHER_FORMAT_LABEL}</option>
          </select>
          {isOther && (
            <input
              className="play-native-input"
              style={{ marginTop: 10 }}
              value={customFormatName}
              onChange={(e) => setCustomFormatName(e.target.value)}
              placeholder="Format name"
              aria-label="Format name"
              maxLength={60}
            />
          )}
        </div>

        <div className="play-field">
          <span className="play-field__label">Structure</span>
          <div className="play-structure-grid" role="radiogroup" aria-label="Structure">
            {STRUCTURE_OPTIONS.map((s) => (
              <button
                key={s.value}
                type="button"
                role="radio"
                aria-checked={structure === s.value}
                className={`play-structure-card ${structure === s.value ? 'play-structure-card--active' : ''}`}
                onClick={() => setStructure(s.value)}
              >
                <span className="play-structure-card__label">{s.label}</span>
                <span className="play-structure-card__desc">{s.description}</span>
              </button>
            ))}
          </div>
          {structure === 'SWISS_TOP_CUT' && (
            <div className="play-topcut-row">
              <span>Top cut</span>
              <SegmentedToggle<'4' | '8'>
                ariaLabel="Top cut size"
                className="segmented-toggle--panel"
                value={String(topCutSize) as '4' | '8'}
                onChange={(v) => setTopCutSize(v === '4' ? 4 : 8)}
                options={[
                  { value: '4', label: 'Top 4' },
                  { value: '8', label: 'Top 8' },
                ]}
              />
            </div>
          )}
        </div>

        <div className="play-card play-card--list play-form__steppers">
        {usesSwiss && (
          <CountStepper
            label="Swiss rounds"
            display={String(swissRounds)}
            hint="Recommended: 3 for up to 8 players, 4 up to 16, 5 up to 32"
            onDecrement={() => setSwissRounds((n) => Math.max(SWISS_ROUNDS_MIN, n - 1))}
            onIncrement={() => setSwissRounds((n) => Math.min(SWISS_ROUNDS_MAX, n + 1))}
            canDecrement={swissRounds > SWISS_ROUNDS_MIN}
            canIncrement={swissRounds < SWISS_ROUNDS_MAX}
          />
        )}
        <CountStepper
          label="Round timer"
          display={formatClock(roundMinutes * 60)}
          hint="Minutes per round"
          onDecrement={() => setRoundMinutes((n) => Math.max(ROUND_MIN_MINUTES, n - ROUND_MINUTES_STEP))}
          onIncrement={() => setRoundMinutes((n) => Math.min(ROUND_MAX_MINUTES, n + ROUND_MINUTES_STEP))}
          canDecrement={roundMinutes > ROUND_MIN_MINUTES}
          canIncrement={roundMinutes < ROUND_MAX_MINUTES}
        />
        </div>

        <div className="play-card play-card--list play-form__toggles">
          <ToggleRow label="Decklists required" sub="Players submit before round 1" checked={decklistRequired} onChange={setDecklistRequired} />
          <ToggleRow label="Late registration" sub="Allow joining until round 2 starts" checked={lateRegistration} onChange={setLateRegistration} />
          <ToggleRow label="Public standings page" sub="Anyone with the link can view" checked={publicPage} onChange={setPublicPage} />
        </div>

        {error && (
          <p className="play-error" role="alert">
            {error}
          </p>
        )}
        <div className="play-sticky-cta">
          <PlayPrimaryButton label="Create & get invite link" type="submit" disabled={!canSubmit} loading={isLoading} testId="play-create-submit" />
        </div>
      </form>
    </PlayLayout>
  );
};

export const ToggleRow: React.FC<{ label: React.ReactNode; sub?: React.ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; ariaLabel?: string }> = ({
  label,
  sub,
  checked,
  onChange,
  disabled,
  ariaLabel,
}) => (
  <div className="play-toggle-row">
    <div className="play-toggle-row__text">
      <span className="play-toggle-row__label">{label}</span>
      {sub && <span className="play-toggle-row__sub">{sub}</span>}
    </div>
    <PlayToggle checked={checked} onChange={onChange} disabled={disabled} ariaLabel={ariaLabel ?? (typeof label === 'string' ? label : undefined)} />
  </div>
);

export default HostCreatePage;
